import { z } from "zod";
import { isValidPostalCode } from "@/lib/validations/postalCode.validation";
import { isValidPhoneNumber } from "libphonenumber-js";
import { countryRequiresPostalCode } from "@/lib/postal-codes.util";
import { i18nMessage } from "@/lib/i18nMessage";

export const customCotationManuelPickupFormSchema = z.object({
  manualPickup: z.literal(true),
  pickupLastName: z
    .string({
      message: i18nMessage("shared:forms.customQuotation.lastNameRequired"),
    })
    .min(1, {
      message: i18nMessage("shared:forms.customQuotation.lastNameRequired"),
    }),
  pickupFirstName: z
    .string({
      message: i18nMessage("shared:forms.customQuotation.firstNameRequired"),
    })
    .min(1, {
      message: i18nMessage("shared:forms.customQuotation.firstNameRequired"),
    }),
  pickupCompany: z.string().optional(),
  pickupPhone: z
    .string({
      message: i18nMessage("shared:forms.customQuotation.phoneRequired"),
    })
    .refine((mobile) => isValidPhoneNumber(mobile), {
      message: i18nMessage("validation:invalidPhoneNumber"),
    }),
  pickupStreet: z
    .string({
      message: i18nMessage("shared:forms.customQuotation.streetRequired"),
    })
    .min(1, {
      message: i18nMessage("shared:forms.customQuotation.streetRequired"),
    }),
  pickupPostalCode: z
    .string({
      message: i18nMessage("shared:forms.customQuotation.postalCodeRequired"),
    })
    .refine((postalCode) => isValidPostalCode(postalCode), {
      message: i18nMessage("validation:invalidPostalCode"),
    }),
  pickupCity: z
    .string({
      message: i18nMessage("shared:forms.customQuotation.cityRequired"),
    })
    .min(1, {
      message: i18nMessage("shared:forms.customQuotation.cityRequired"),
    }),
  pickupCountry: z
    .string({
      message: i18nMessage("shared:forms.customQuotation.pickupCountryRequired"),
    })
    .min(1, {
      message: i18nMessage("shared:forms.customQuotation.pickupCountryRequired"),
    }),
});

export const customCotationPickupLocationIdFormSchema = z.object({
  manualPickup: z.literal(false),
  pickupLocationId: z
    .string({
      message: i18nMessage("shared:forms.customQuotation.pickupLocationRequired"),
    })
    .min(1, i18nMessage("shared:forms.customQuotation.pickupLocationRequired")),
});

const deliveryInfoSchema = z
  .object({
    lastName: z
      .string({ message: i18nMessage("shared:forms.customQuotation.lastNameRequired") })
      .min(1, i18nMessage("shared:forms.customQuotation.lastNameRequired")),
    firstName: z
      .string({ message: i18nMessage("shared:forms.customQuotation.firstNameRequired") })
      .min(1, i18nMessage("shared:forms.customQuotation.firstNameRequired")),
    company: z.string().optional(),
    phone: z
      .string({
        message: i18nMessage("shared:forms.customQuotation.phoneRequired"),
      })
      .refine((mobile) => isValidPhoneNumber(mobile), {
        message: i18nMessage("validation:invalidPhoneNumber"),
      }),
    email: z
      .string({
        message: i18nMessage("shared:forms.customQuotation.emailRequired"),
      })
      .email({ message: i18nMessage("validation:invalidEmail") })
      .min(1, i18nMessage("shared:forms.customQuotation.emailRequired")),
    street: z
      .string({
        message: i18nMessage("shared:forms.customQuotation.streetRequired"),
      })
      .min(1, i18nMessage("shared:forms.customQuotation.streetRequired")),
    additionalInfo: z.string().optional(),
    additionalInfo2: z.string().optional(),
    postalCode: z.string().optional(),
    city: z
      .string({
        message: i18nMessage("shared:forms.customQuotation.cityRequired"),
      })
      .min(1, i18nMessage("shared:forms.customQuotation.cityRequired")),
    countryCode: z.string().min(1, i18nMessage("shared:forms.customQuotation.countryCodeRequired")),
    stateOrProvinceCode: z.string().optional(),
    additionalEmail: z
      .string()
      .email({ message: i18nMessage("validation:invalidEmail") })
      .optional(),
    additionalPhone: z
      .string()
      .refine((phone) => isValidPhoneNumber(phone), {
        message: i18nMessage("validation:invalidPhoneNumber"),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (
      (data.countryCode === "CA" || data.countryCode === "US") &&
      (!data.stateOrProvinceCode || data.stateOrProvinceCode.length !== 2)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("validation:stateOrProvinceRequired"),
        path: ["stateOrProvinceCode"],
      });
    }
    if (countryRequiresPostalCode(data.countryCode) && !data.postalCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("validation:postalCodeRequiredForCountry"),
        path: ["postalCode"],
      });
    }
  });

export const parcelBottleProductSchema = z.object({
  type: z.literal("BOTTLE"),
  volume: z.number({
    coerce: true,
    message: i18nMessage("shared:forms.customQuotation.volumeRequired"),
  }),
  bottleQuantity: z.number({
    coerce: true,
    message: i18nMessage("shared:forms.customQuotation.bottleQuantityRequired"),
  }),
  description: z.string().optional().nullable(),
});

export const parcelOtherProductSchema = z.object({
  type: z.literal("OTHER"),
  description: z.string({
    message: i18nMessage("shared:forms.customQuotation.descriptionRequired"),
  }),
  volume: z.number().optional().nullable(),
  bottleQuantity: z.number().optional().nullable(),
});

export const CustomCotationFormSchema = z.object({
  pickupInfo: z.discriminatedUnion("manualPickup", [
    customCotationManuelPickupFormSchema,
    customCotationPickupLocationIdFormSchema,
  ]),
  billingEntityId: z
    .string({
      message: i18nMessage("shared:forms.customQuotation.billingEntityRequired"),
    })
    .min(1, i18nMessage("shared:forms.customQuotation.billingEntityRequired")),
  deliveryInfo: deliveryInfoSchema,
  insurance: z.boolean(),
  express: z.boolean(),
  parcelProducts: z
    .array(z.discriminatedUnion("type", [parcelBottleProductSchema, parcelOtherProductSchema]))
    .optional(),
  parcelPackages: z
    .array(
      z.object({
        length: z
          .number({
            coerce: true,
            message: i18nMessage("shared:forms.customQuotation.parcelLengthRequired"),
          })
          .min(1, i18nMessage("shared:forms.customQuotation.paletteLengthRequired")),
        width: z
          .number({
            coerce: true,
            message: i18nMessage("shared:forms.customQuotation.parcelWidthRequired"),
          })
          .min(1, i18nMessage("shared:forms.customQuotation.paletteWidthRequired")),
        height: z
          .number({
            coerce: true,
            message: i18nMessage("shared:forms.customQuotation.parcelHeightRequired"),
          })
          .min(0, i18nMessage("shared:forms.customQuotation.paletteHeightRequired")),
        weight: z
          .number({
            coerce: true,
            message: i18nMessage("shared:forms.customQuotation.parcelWeightRequired"),
          })
          .min(0, i18nMessage("shared:forms.customQuotation.paletteWeightRequired")),
        value: z
          .number({
            coerce: true,
            message: i18nMessage("shared:forms.customQuotation.parcelValueRequired"),
          })
          .min(0, i18nMessage("shared:forms.customQuotation.parcelValueRequired")),
      }),
    )
    .optional(),
  paletteProducts: z
    .array(
      z.object({
        volume: z
          .number({
            coerce: true,
            message: i18nMessage("shared:forms.customQuotation.volumeRequired"),
          })
          .gt(0, i18nMessage("shared:forms.customQuotation.bottleVolumeRequired")),
        bottleQuantity: z
          .number({
            coerce: true,
            message: i18nMessage("shared:forms.customQuotation.bottleQuantityRequired"),
          })
          .min(1, i18nMessage("shared:forms.customQuotation.bottleQuantityRequired")),
      }),
    )
    .optional(),
  palettePackages: z
    .array(
      z.object({
        length: z
          .number({
            coerce: true,
            message: i18nMessage("shared:forms.customQuotation.paletteLengthRequired"),
          })
          .min(1, i18nMessage("shared:forms.customQuotation.paletteLengthRequired")),
        width: z
          .number({
            coerce: true,
            message: i18nMessage("shared:forms.customQuotation.paletteWidthRequired"),
          })
          .min(1, i18nMessage("shared:forms.customQuotation.paletteWidthRequired")),
        height: z
          .number({
            coerce: true,
            message: i18nMessage("shared:forms.customQuotation.paletteHeightRequired"),
          })
          .min(0, i18nMessage("shared:forms.customQuotation.paletteHeightRequired")),
        weight: z
          .number({
            coerce: true,
            message: i18nMessage("shared:forms.customQuotation.paletteWeightRequired"),
          })
          .min(0, i18nMessage("shared:forms.customQuotation.paletteWeightRequired")),
        value: z
          .number({
            coerce: true,
            message: i18nMessage("shared:forms.customQuotation.paletteValueRequired"),
          })
          .min(0, i18nMessage("shared:forms.customQuotation.paletteValueRequired")),
      }),
    )
    .optional(),
  comment: z.string().optional(),
  reference: z.string().optional(),
  customId: z
    .string({
      message: i18nMessage("shared:forms.customQuotation.destinationRequired"),
    })
    .min(1, i18nMessage("shared:forms.customQuotation.destinationRequired")),
  newFilesId: z.array(z.string()).optional(),
  removedFilesId: z.array(z.string()).optional(),
});

export type CustomCotationFormData = z.infer<typeof CustomCotationFormSchema>;
